import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { Box, Typography, Paper, Alert, CircularProgress, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api/config';

interface GoogleCredentialResponse {
  credential: string;
}

interface DecodedCredential {
  email: string;
  name: string;
  picture: string;
  sub: string;
}

interface AuthResponse {
  token: string;
  userId: number;
  name: string;
  email: string;
  organisationId: number;
  role: string;
}

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);
  const handleGoogleLogin = async (credentialResponse: any) => {
    try {
      setLoading(true);
      setError(null);
      
      // Get the credential from Google's response
      
      const decoded = jwtDecode<DecodedCredential>(credentialResponse.credential);
      const request = {
        token: credentialResponse.credential,
        email: decoded.email,
        name: decoded.name,
      };
      
      
      // Send the Google token to your backend
      const response = await apiClient.post('/api/auth/google', request);
      
      // Assuming your backend returns a JWT token and user info
      const { token, userId, name, email, organisationId } = response.data;
      
      // Store the token and user info in your Auth context
      login(token, {
        id: userId,
        name: name,
        email: email,
        organisationId: organisationId,
        role: 'user'
      });
      
      navigate('/dashboard');
    } catch (err) {
      console.error('Login failed:', err);
      setError(t('loginFailed'));
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '80vh' 
    }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          {t('login')}
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom sx={{ mb: 3 }}>
          {t('organisation.login.subtitle')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 2 
        }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <Typography variant="body2" align="center" sx={{ mb: 2 }}>
                {t('loginWithGoogle')}
              </Typography>
              
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => setError(t('googleLoginFailed'))}
                theme="filled_blue"
                shape="rectangular"
                context="signin"
                width="250px"
              />
              
              <Divider sx={{ width: '100%', my: 2 }} />
              
              <Typography variant="caption" color="text.secondary" align="center">
                {t('loginDisclaimer')}
              </Typography>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;